import { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api, { refreshAccessToken } from "../../services/api";
import toastError from "../../errors/toastError";
import { SocketContext, SocketManager } from "../../context/Socket/SocketContext";
import { clearSessionKeys } from "../../helpers/sessionStorageKeys";
import {
  isLicenseError,
  licenseReasonFrom,
  setLicenseNotice,
} from "../../helpers/licenseNotice";
import moment from "moment";

// Flag global para controlar se os interceptors já foram configurados
let interceptorsConfigured = false;

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [menus, setMenus] = useState([]);
  // `configLoaded: false` = ainda não sabemos o que o servidor usa. Antes o estado
  // inicial já vinha com 15 minutos chumbados, então uma falha em
  // /auth/session-config se disfarçava de resposta real e o indicador do topo
  // mostrava "15min" sem que o servidor jamais tivesse dito isso.
  const [sessionConfig, setSessionConfig] = useState({
    configLoaded: false,
    timeoutDisabled: null,
    tokenExpiresIn: null,
    timeoutMinutes: null,
    message: ""
  });

  // Função para normalizar menus (pode vir como array ou string JSON)
  const normalizeMenus = (menusData) => {
    if (Array.isArray(menusData)) return menusData;
    if (typeof menusData === 'string' && menusData) {
      try {
        const parsed = JSON.parse(menusData);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.warn('Erro ao parsear menus:', e);
        return [];
      }
    }
    return [];
  };

  // Função para buscar configuração de sessão (sem autenticação).
  // Puramente informativa: o fluxo de autenticação NÃO depende mais dela.
  const fetchSessionConfig = async () => {
    try {
      const { data } = await api.get("/auth/session-config");
      const loaded = { ...data, configLoaded: true };
      setSessionConfig(loaded);
      return loaded;
    } catch (error) {
      console.warn("Não foi possível obter configuração de sessão:", error);
      const fallback = {
        configLoaded: false,
        timeoutDisabled: null,
        tokenExpiresIn: null,
        timeoutMinutes: null,
        message: "Não foi possível obter a configuração de sessão do servidor"
      };
      // O catch antigo devolvia o fallback mas nunca o gravava no estado, então
      // a tela continuava exibindo o padrão chumbado como se fosse real.
      setSessionConfig(fallback);
      return fallback;
    }
  };

  // Configura interceptors apenas uma vez
  useEffect(() => {
    // Se já foi configurado, não configura novamente
    if (interceptorsConfigured) {
      return;
    }
    
    interceptorsConfigured = true;
    
    // Interceptor de requisição
    api.interceptors.request.use(
      (config) => {
        // Não adiciona token para rotas públicas
        const publicRoutes = ['/auth/login', '/auth/session-config', '/auth/signup', '/auth/clear-session'];
        const isPublicRoute = publicRoutes.some(route => config.url?.includes(route));
        
        if (!isPublicRoute) {
          const token = localStorage.getItem("token");
          if (token) {
            try {
              config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
            } catch (e) {
              console.warn("Token inválido no localStorage, removendo");
              localStorage.removeItem("token");
            }
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    
    // Interceptor de resposta
    api.interceptors.response.use(
      (response) => {
        // Verifica se há um novo token no header de resposta
        const newToken = response.headers['x-new-token'] || response.headers['X-New-Token'];
        if (newToken) {
          localStorage.setItem("token", JSON.stringify(newToken));
          api.defaults.headers.Authorization = `Bearer ${newToken}`;
          // O socket guarda o token do handshake: sem reaplicá-lo aqui, ele
          // seguiria tentando reconectar com a credencial antiga.
          SocketManager.applyToken(newToken);
          console.log("Token renovado automaticamente via header");
        }
        return response;
      },
      (error) => {
        // NÃO faz redirecionamento automático aqui
        // O redirecionamento será tratado apenas no useEffect de inicialização
        // para evitar loops na página de login
        return Promise.reject(error);
      }
    );
    
    // Cleanup não remove interceptors para evitar problemas
    // já que eles são globais e compartilhados
  }, []);

  const socketManager = useContext(SocketContext);

  // Efeito para verificar autenticação na inicialização
  useEffect(() => {
    const tokenRaw = localStorage.getItem("token");
    
    // Verifica se o token existe E é válido (não vazio, não "null", não "undefined")
    let token = null;
    if (tokenRaw && tokenRaw !== "null" && tokenRaw !== "undefined" && tokenRaw.trim() !== "") {
      try {
        // Tenta parsear (token é armazenado como JSON string)
        token = JSON.parse(tokenRaw);
        if (!token || token === "null" || token === "undefined") {
          token = null;
        }
      } catch (e) {
        // Se não é JSON válido, pode ser o token direto
        token = tokenRaw;
      }
    }
    
    // Se estamos na página de login (apenas /login, não /)
    const isLoginPage = window.location.pathname === "/login";
    
    (async () => {
      // NOTA: o atalho antigo "página de login + token válido" foi removido.
      // Ele resolvia o usuário por /auth/me e retornava sem chamar
      // fetchSessionConfig(), sem setMenus() e sem rotacionar o token — a sessão
      // ficava com o token antigo e sem menus. Agora /login segue o mesmo boot
      // das demais rotas e o próprio componente Login redireciona para /tickets
      // quando isAuth fica verdadeiro.

      // Se não há token válido, apenas marca como não autenticado
      // NÃO seta sessionExpired - o usuário nunca teve sessão
      if (!token) {
        // Limpa qualquer flag residual e tokens inválidos
        // Também limpa companyId e userId para evitar falsos positivos de sessão ativa
        clearSessionKeys();
        // Fora da tela de login não há para quem mostrar o aviso de sessão
        // expirada; nela, quem apaga a flag é o próprio componente Login, depois
        // de exibi-la.
        if (!isLoginPage) {
          localStorage.removeItem("sessionExpired");
        }

        // IMPORTANTE: Limpa o cookie de refresh token para evitar que um usuário
        // seja autenticado com as credenciais de outro usuário que estava logado antes
        try {
          await api.post("/auth/clear-session");
        } catch (e) {
          // Ignora erro - pode não haver sessão para limpar
          console.log("Nenhuma sessão anterior para limpar");
        }
        
        setIsAuth(false);
        setUser({});
        setMenus([]);
        setLoading(false);
        return;
      }
      
      // Configuração de sessão: só alimenta o indicador do topo. O boot abaixo é
      // igual nos dois modos, de propósito.
      fetchSessionConfig();

      // Decodifica o token atual para saber qual usuário DEVERIA estar logado
      let expectedUserId = null;
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        expectedUserId = tokenPayload.id;
      } catch (e) {
        // Token inválido - silencioso
      }
      
      try {
        let userData = null;

        // CAMINHO ÚNICO, nos dois modos de sessão.
        //
        // Antes, com "sessão prolongada" ligada, o boot usava
        // /auth/session-status + /auth/me e mantinha o token ANTIGO no
        // localStorage. Como o socket (cliente e servidor) confere validade e
        // tokenVersion, um token defasado por outro login deixava o REST
        // funcionando e o tempo real morto: usuário logado, nenhuma conversa,
        // nenhum contato, nenhum atendimento.
        //
        // /auth/refresh_token é o único endpoint que devolve, de uma vez, um
        // token recém-cunhado com o tokenVersion atual do banco, o usuário
        // serializado (com queues/whatsapps) e o cookie jrt rotacionado.
        try {
          const { data } = await api.post("/auth/refresh_token", {}, {
            headers: { Authorization: `Bearer ${token}` }
          });

          // IMPORTANTE: Verifica se o usuário retornado é o esperado
          if (expectedUserId && data.user.id !== expectedUserId) {
            console.error(`[useAuth] CONFLITO DE USUÁRIOS! Token é do usuário ${expectedUserId}, mas refresh_token retornou usuário ${data.user.id}. Forçando logout.`);
            throw new Error("USER_MISMATCH");
          }

          userData = data.user;
          localStorage.setItem("token", JSON.stringify(data.token));
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
        } catch (refreshError) {
          // 401 = sessão realmente encerrada (expirada ou substituída por outro
          // login). Vai direto para o catch de baixo, que limpa e manda para o
          // login. Cair no /auth/me aqui é exatamente o que criava a sessão
          // meio-quebrada.
          if (
            refreshError?.response?.status === 401 ||
            refreshError.message === "USER_MISMATCH"
          ) {
            throw refreshError;
          }

          // Rede fora do ar ou 5xx: mantém o usuário na tela com o token atual
          // em vez de deslogar por uma falha passageira.
          console.warn(
            "Falha ao renovar o token no boot; usando /auth/me como contingência:",
            refreshError?.response?.status || refreshError?.message
          );
          api.defaults.headers.Authorization = `Bearer ${token}`;
          const { data: meData } = await api.get("/auth/me");

          if (expectedUserId && meData.id !== expectedUserId) {
            console.error(`[useAuth] CONFLITO DE USUÁRIOS! Token é do usuário ${expectedUserId}, mas /auth/me retornou usuário ${meData.id}. Forçando logout.`);
            throw new Error("USER_MISMATCH");
          }

          userData = meData;
        }

        if (userData) {
          // Atualiza companyId e userId no localStorage para manter consistência
          localStorage.setItem("companyId", userData.companyId);
          localStorage.setItem("userId", userData.id);
          
          setIsAuth(true);
          setUser(userData);
          setMenus(normalizeMenus(userData.menus));
        }
      } catch (err) {
        console.error("Erro na autenticação:", err);
        
        // Verifica se é um erro real de sessão expirada (401)
        const isSessionExpiredError = err?.response?.status === 401;
        
        // Verifica se havia uma SESSÃO ATIVA antes (não apenas um token qualquer)
        // Para considerar uma sessão ativa, verificamos se:
        // 1. Havia um token com formato válido (mais de 50 chars, típico de JWT)
        // 2. Havia companyId (indica que usuário estava logado de verdade)
        // 3. Havia userId (confirma sessão ativa anterior)
        const hadCompanyId = !!localStorage.getItem("companyId");
        const hadUserId = !!localStorage.getItem("userId");
        const tokenLooksValid = !!token && typeof token === 'string' && token.length > 50;
        
        // Só considera sessão válida se tinha TODOS os indicadores de login ativo
        const hadActiveSession = tokenLooksValid && hadCompanyId && hadUserId;
        
        console.log("Debug sessionExpired:", {
          hadActiveSession,
          tokenLooksValid,
          hadCompanyId,
          hadUserId,
          isSessionExpiredError,
          isLoginPage,
          tokenLength: token?.length,
          errorStatus: err?.response?.status
        });
        
        // Limpa token inválido
        clearSessionKeys();
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
        setUser({});
        setMenus([]);
        
        // Só seta sessionExpired se TODAS as condições forem verdadeiras:
        // 1. Havia uma sessão ATIVA (token válido + companyId + userId)
        // 2. Foi um erro 401 (token expirado/inválido)
        // 3. Não estamos na página de login
        // NÃO seta para: primeira visita, logout manual, erros de rede, tokens residuais
        if (hadActiveSession && isSessionExpiredError && !isLoginPage) {
          console.log("Setando sessionExpired = true - sessão ativa expirou");
          localStorage.setItem("sessionExpired", "true");
        } else {
          // Garante que a flag está limpa se não deve mostrar mensagem
          console.log("Limpando sessionExpired - não era uma sessão ativa ou já está na página de login");
          localStorage.removeItem("sessionExpired");
        }
      }
      
      setLoading(false);
    })();
  }, []);

  // O SocketManager é singleton de módulo e não enxerga o React: registra aqui a
  // ponte para ele pedir renovação quando encontrar um token vencido.
  useEffect(() => {
    SocketManager.onTokenStale = () => {
      refreshAccessToken();
    };
    return () => {
      SocketManager.onTokenStale = null;
    };
  }, []);

  // Renovação periódica, nos DOIS modos.
  //
  // O interceptor do api.js já renova e repete a requisição quando alguma falha
  // por token vencido, mas isso só acontece se houver tráfego. Sem este timer,
  // uma aba parada passaria do vencimento e o socket — que confere validade no
  // handshake — não conseguiria mais reconectar até a próxima requisição.
  //
  // O intervalo acompanha o tempo real da sessão: em modo prolongado (30 dias)
  // 5 minutos bastam; no modo normal renova a ~60% do prazo, para nunca esbarrar
  // no vencimento. Antes esta renovação existia só no modo prolongado, e era
  // justamente por isso que o modo normal caía em 403 sem fim.
  useEffect(() => {
    if (!isAuth || !sessionConfig.configLoaded) {
      return undefined;
    }

    const fiveMinutes = 5 * 60 * 1000;
    const intervalMs = sessionConfig.timeoutDisabled
      ? fiveMinutes
      : Math.max(
          60 * 1000,
          Math.round((sessionConfig.timeoutMinutes || 15) * 60 * 1000 * 0.6)
        );

    const intervalId = setInterval(() => {
      refreshAccessToken();
    }, intervalMs);

    return () => clearInterval(intervalId);
  }, [
    isAuth,
    sessionConfig.configLoaded,
    sessionConfig.timeoutDisabled,
    sessionConfig.timeoutMinutes
  ]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId || !user?.id) {
      return undefined;
    }

    const socket = socketManager.getSocket(companyId);
    const userEvent = `company-${companyId}-user`;
    const authEvent = `company-${companyId}-auth`;

    const handleUserEvent = (data) => {
      if (data?.action !== "update" || Number(data?.user?.id) !== Number(user.id)) {
        return;
      }

      // Os menus acompanham o perfil: quando um admin troca o perfil de quem
      // está logado, aplicar só o `profile` deixaria a tela avaliando permissão
      // com perfil novo e menus antigos, até o próximo login.
      if (Array.isArray(data.user?.menus)) {
        setMenus(normalizeMenus(data.user.menus));
      }

      setUser((prevUser) => {
        if (!prevUser || typeof prevUser !== "object") {
          return data.user;
        }

        const mergedQueues = Array.isArray(data.user?.queues)
          ? data.user.queues
          : Array.isArray(prevUser.queues)
            ? prevUser.queues
            : [];

        // Conexões gerenciadas: alimentam o escopo por conexão da lista de
        // tickets. Sem preservá-las, uma atualização parcial do usuário faria
        // sumir os tickets sem fila das conexões extras.
        const mergedWhatsapps = Array.isArray(data.user?.whatsapps)
          ? data.user.whatsapps
          : Array.isArray(prevUser.whatsapps)
            ? prevUser.whatsapps
            : [];

        return {
          ...prevUser,
          ...data.user,
          queues: mergedQueues,
          whatsapps: mergedWhatsapps
        };
      });

      // Filas/conexões/allTicket podem ter mudado: o socket precisa refazer os
      // joins, senão continua recebendo (ou deixando de receber) os tickets
      // errados até a próxima reconexão.
      socket.emit("refreshUserRooms");
    };

    const handleAuthEvent = (data) => {
      if (Number(data?.user?.id) !== Number(user.id)) {
        return;
      }

      if (data.action === "force-logout") {
        console.warn("Logout forçado detectado no hook de auth");
        // Licença Empsis bloqueada pelo cron/super admin: a tela de login
        // mostra o motivo. O handleLogout não apaga este recado.
        if (data.reason === "license") {
          setLicenseNotice(data.message);
        }
        handleLogout();
      }
    };

    socket.on(userEvent, handleUserEvent);
    socket.on(authEvent, handleAuthEvent);

    return () => {
      socket.off(userEvent, handleUserEvent);
      socket.off(authEvent, handleAuthEvent);
    };
  }, [socketManager, user?.id]);

  const handleLogin = async (userData) => {
    setLoading(true);
    
    console.log("========== HANDLE LOGIN INICIADO ==========");
    console.log("Credenciais enviadas:", { email: userData.email, password: "***" });
    
    // IMPORTANTE: Limpa TODOS os dados anteriores antes de fazer novo login
    // Isso evita problemas de usuários trocados
    clearSessionKeys();
    // O socket da sessão anterior carrega o token antigo no handshake: sem
    // derrubá-lo, ele continuaria tentando reconectar com a credencial que o
    // novo login acabou de invalidar.
    SocketManager.reset();
    api.defaults.headers.Authorization = undefined;
    delete api.defaults.headers.common['Authorization'];
    
    console.log("localStorage limpo, estado antes do login:");
    console.log("- token:", localStorage.getItem("token"));
    console.log("- companyId:", localStorage.getItem("companyId"));
    console.log("- userId:", localStorage.getItem("userId"));
    
    // Limpa o cookie de sessão anterior para evitar conflitos de usuários
    try {
      await api.post("/auth/clear-session");
      console.log("Cookie de sessão limpo via /auth/clear-session");
    } catch (e) {
      console.log("Erro ao limpar sessão anterior:", e);
    }
    
    try {
      console.log("Fazendo POST para /auth/login...");
      const { data } = await api.post("/auth/login", userData);
      
      console.log("========== RESPOSTA DO LOGIN ==========");
      console.log("data.user.id:", data.user.id);
      console.log("data.user.email:", data.user.email);
      console.log("data.user.name:", data.user.name);
      console.log("data.user.companyId:", data.user.companyId);

      const {
        user: { companyId, id, company },
      } = data;
      
      console.log("Valores extraídos: companyId=", companyId, "id=", id);

      if (has(company, "settings") && isArray(company.settings)) {
        const setting = company.settings.find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null); //regra pra exibir campanhas
        }
      }

      const dueDate = data.user.company.dueDate;
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      var diff = moment(dueDate).diff(moment(moment()).format());

      var before = moment(moment().format()).isBefore(dueDate);
      var dias = moment.duration(diff).asDays();

      // O login sempre completa; empresa bloqueada (vencimento + carência,
      // flag companyBlocked calculada no backend) cai direto no Financeiro
      // para regularizar o pagamento.
      const companyBlocked = data.user.companyBlocked === true;

      localStorage.setItem("token", JSON.stringify(data.token));
      localStorage.setItem("companyId", companyId);
      localStorage.setItem("userId", id);
      localStorage.setItem("companyDueDate", vencimento);
      localStorage.removeItem("sessionExpired"); // Limpa flag de sessão expirada
      api.defaults.headers.Authorization = `Bearer ${data.token}`;

      setUser(data.user);
      setMenus(normalizeMenus(data.user.menus));
      setIsAuth(true);
      setLoading(false);

      if (companyBlocked) {
        toastError(i18n.t("auth.toasts.subscriptionBlocked", { date: vencimento }));
        window.location.href = "/financeiro";
      } else {
        toast.success(i18n.t("auth.toasts.success"));
        if (before === true && Math.round(dias) < 5) {
          toast.warn(
            i18n.t(
              Math.round(dias) === 1
                ? "auth.toasts.subscriptionExpiresInOneDay"
                : "auth.toasts.subscriptionExpiresInDays",
              { days: Math.round(dias) }
            )
          );
        }
        if (before !== true) {
          // vencida, mas dentro da carência: avisa sem bloquear
          toast.warn(i18n.t("auth.toasts.subscriptionExpired", { date: vencimento }));
        }

        // Autor: GitHub Copilot | Data: 26/03/2026 | Motivo: remover abertura automática da popup de follow-up no login.

        // Usa window.location para garantir navegação após login
        // history.push pode não funcionar corretamente durante mudança de estado
        window.location.href = "/tickets";
      }

      //quebra linha 
    } catch (err) {
      setLoading(false);
      // Licença Empsis inválida: aviso fixo na tela de login, com o motivo da
      // Empsis, em vez de um toast que some em segundos.
      if (isLicenseError(err)) {
        setLicenseNotice(licenseReasonFrom(err));
        return;
      }
      toastError(err);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      await api.delete("/auth/logout");
    } catch (err) {
      console.warn("Erro ao fazer logout no backend:", err);
    }
    
    // Sempre limpa o estado local, independente do resultado da API
    clearSessionKeys();
    // Logout manual não é sessão expirada: não deixa o aviso para a tela de login.
    localStorage.removeItem("sessionExpired");
    SocketManager.reset();

    // Limpa completamente os headers do axios
    api.defaults.headers.Authorization = undefined;
    delete api.defaults.headers.common['Authorization'];
    
    setIsAuth(false);
    setUser({});
    setMenus([]);
    setLoading(false);
    
    // Usa window.location para garantir um refresh completo
    window.location.href = "/login";
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser((prev) => ({ ...prev, ...data }));
      setMenus(normalizeMenus(data.menus));
      return data;
    } catch (err) {
      toastError(err);
    }
  };

  return {
    isAuth,
    user,
    loading,
    menus,
    sessionConfig,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
  };
};

export default useAuth;
